import React, { useEffect, useRef } from 'react';

/**
 * StarfieldBackground
 * Kräftiges 3D-Warp-Sternenfeld (Canvas): Sterne schießen aus der Bildmitte
 * nach außen (Hyperspace), mit langen Streifen für starke Tiefenwirkung,
 * plus lebendige farbige Atmosphäre/Nebel (CSS, animiert).
 *
 * Performance: Mobile weniger Sterne + ~30 FPS + niedrigeres DPR; Pause bei
 * verstecktem Tab; läuft IMMER (auch reduce-motion -> nur ruhiger).
 */
const StarfieldBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const isMobile = window.innerWidth < 768;
    const reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionScale = reduceMotion ? 0.55 : 1; // nie ganz einfrieren
    const TRAIL = isMobile ? 1.4 : 2.0;          // Streifen-Verlängerung (3D-Look)

    let W = 0, H = 0, DPR = 1, cx = 0, cy = 0, fov = 1;
    let stars = [];
    let raf = 0, running = false, prev = 0;

    const COLORS = [
      '255,255,255', '255,255,255',  // weiß
      '180,225,255',                 // cyan-blau
      '0,255,209',                   // SolVibe cyan
      '20,241,149',                  // SolVibe grün
      '201,168,255',                 // lila
    ];

    const resetStar = (s) => {
      s.x = Math.random() * 2 - 1;
      s.y = Math.random() * 2 - 1;
      s.z = 1;
      s.sp = Math.random() * 0.0035 + 0.0025;   // Fluggeschwindigkeit
      s.col = COLORS[(Math.random() * COLORS.length) | 0];
      s.px = cx + (s.x / s.z) * fov;
      s.py = cy + (s.y / s.z) * fov;
      return s;
    };

    const buildStars = () => {
      const count = isMobile ? 90 : 320;
      stars = [];
      for (let i = 0; i < count; i++) {
        const s = resetStar({});
        s.z = Math.random() * 0.9 + 0.1;          // gestreute Starttiefe
        s.px = cx + (s.x / s.z) * fov;
        s.py = cy + (s.y / s.z) * fov;
        stars.push(s);
      }
    };

    const resize = () => {
      DPR = Math.min(window.devicePixelRatio || 1, isMobile ? 1.0 : 1.5);
      W = canvas.width = Math.floor(window.innerWidth * DPR);
      H = canvas.height = Math.floor(window.innerHeight * DPR);
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      cx = W / 2; cy = H / 2;
      fov = Math.min(W, H) * 0.6;
      buildStars();
    };

    const draw = (dt) => {
      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        s.z -= s.sp * motionScale * dt;
        let recycled = false;
        if (s.z <= 0.04) { resetStar(s); recycled = true; }

        const sx = cx + (s.x / s.z) * fov;
        const sy = cy + (s.y / s.z) * fov;
        if (recycled) { s.px = sx; s.py = sy; }

        const depth = 1 - s.z;
        const size = depth * depth * 3.0 * DPR + 0.7;
        let a = depth * 1.3 + 0.28;
        if (a > 1) a = 1;

        if (sx > -60 && sx < W + 60 && sy > -60 && sy < H + 60) {
          const color = 'rgba(' + s.col + ',' + a + ')';
          // verlängerter Warp-Streifen für 3D-Tiefe
          const dx = (sx - s.px) * TRAIL;
          const dy = (sy - s.py) * TRAIL;
          ctx.strokeStyle = color;
          ctx.lineWidth = size;
          ctx.beginPath();
          ctx.moveTo(sx - dx, sy - dy);
          ctx.lineTo(sx, sy);
          ctx.stroke();
          // heller Kopf
          ctx.fillStyle = color;
          ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        }
        s.px = sx; s.py = sy;
      }
    };

    const frame = (ts) => {
      if (!running) return;
      if (!prev) prev = ts;
      let dt = (ts - prev) / 16.667;   // 1 = 60-FPS-Tempo, unabhängig von der Bildrate
      prev = ts;
      if (dt > 3) dt = 3;              // große Sprünge (Lag/Tab-Wechsel) begrenzen
      if (dt < 0) dt = 1;
      draw(dt);
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (running) return; running = true; prev = 0; raf = requestAnimationFrame(frame); };
    const stop = () => { running = false; prev = 0; if (raf) cancelAnimationFrame(raf); };
    const onVisibility = () => { if (document.hidden) stop(); else start(); };

    resize();
    // Perf: kurz nach React-Mount im Idle starten (nicht erst window.load —
    // das feuert auf langsamem 4G erst nach ~4s und drückte den Speed Index,
    // weil der Hintergrund spät "auflebte"). TBT ist 0ms, das ist verkraftbar.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => requestAnimationFrame(start), { timeout: 900 });
    } else {
      setTimeout(() => requestAnimationFrame(start), 300);
    }
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <>
      {/* Kräftige, animierte Atmosphäre / Nebel (CSS) */}
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none svb-nebula svb-nebula-a" style={{ zIndex: 0 }} />
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none svb-nebula svb-nebula-b" style={{ zIndex: 0 }} />
      {/* Warp-Sternenfeld */}
      <canvas ref={canvasRef} aria-hidden="true" className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }} />
      <style>{`
        .svb-nebula { will-change: transform, opacity; }
        .svb-nebula-a {
          background:
            radial-gradient(58% 52% at 16% 16%, rgba(153, 69, 255, 0.42) 0%, transparent 66%),
            radial-gradient(55% 50% at 86% 26%, rgba(0, 255, 209, 0.26) 0%, transparent 68%);
          animation: svbNebA 24s ease-in-out infinite alternate;
        }
        .svb-nebula-b {
          background:
            radial-gradient(62% 55% at 64% 90%, rgba(20, 241, 149, 0.30) 0%, transparent 70%),
            radial-gradient(50% 46% at 28% 78%, rgba(153, 69, 255, 0.26) 0%, transparent 70%);
          animation: svbNebB 32s ease-in-out infinite alternate;
        }
        @keyframes svbNebA {
          0%   { transform: translate3d(-4%, -3%, 0) scale(1.05); opacity: 0.85; }
          100% { transform: translate3d(4%, 3%, 0) scale(1.2);  opacity: 1; }
        }
        @keyframes svbNebB {
          0%   { transform: translate3d(3%, 4%, 0) scale(1.12); opacity: 0.75; }
          100% { transform: translate3d(-3%, -4%, 0) scale(1.0); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .svb-nebula-a, .svb-nebula-b { animation-duration: 70s; }
        }
      `}</style>
    </>
  );
};

export default React.memo(StarfieldBackground);
